import React, { useMemo } from 'react';
import { Notification } from '../../../ui-toolkit/components';
import { eDiscountType } from '../../../ui-toolkit/discounts/types/eDiscountType';
import { useParams } from 'react-router-dom';
import { UUID } from '../../../types/shared/UUID';
import { useGetDiscountsQuery } from '../../../API/order/fees/DiscountApi';
import { ErrorMessageTW } from '../../../tailwindComponents/errorMessage/ErrorMessageTW';
import { RootStore } from '../../../store/types';
import { useSelector } from 'react-redux';
import { SerializedError } from '@reduxjs/toolkit';
import { FetchBaseQueryError } from '@reduxjs/toolkit/dist/query';

const useFindDiscountsError = () => {
  const { orderId } = useParams<{ orderId: UUID }>();
  const discountsRedux = useSelector(({ order: { discounts } }: RootStore) => discounts);
  return useMemo(() => {
    const updateErrors = discountsRedux.updateDiscountsError;
    if (orderId in updateErrors) {
      return updateErrors[orderId];
    }
    return null;
  }, [orderId, discountsRedux]);
};

const useGetReissueDiscountData = (): {
  discountAvailable: boolean;
  discountApplied: boolean;
  error?: SerializedError | FetchBaseQueryError;
} => {
  const { orderId } = useParams<{ orderId: UUID }>();
  const { data: discounts = [], error } = useGetDiscountsQuery(orderId);
  return useMemo(() => {
    const reissueDiscount = (discounts ?? []).find(
      ({ discountType }) => discountType === eDiscountType.ReissueDiscount
    );
    if (!reissueDiscount) {
      return {
        discountAvailable: false,
        discountApplied: false,
        error,
      };
    }
    const discountApplied = !!reissueDiscount.applied;
    return { error, discountApplied: discountApplied, discountAvailable: !discountApplied };
  }, [discounts, error]);
};

export const ReissueDiscountAlert: React.FC = () => {
  const { discountAvailable, discountApplied, error } = useGetReissueDiscountData();
  const updateError = useFindDiscountsError();

  return (
    <>
      <ErrorMessageTW rtkError={error} />
      {discountAvailable && (
        <Notification type={'warning'} mode={'inverted'}>
          Lender’s Policy may be eligible for a discount.
        </Notification>
      )}
      {discountApplied && (
        <Notification type={'success'} mode={'inverted'}>
          Policy discount applied. Prior to closing, apply any changes to the Discount section and recalculate fees.
        </Notification>
      )}
      {updateError && (
        <Notification type={'error'} mode={'inverted'}>
          This order is not eligible for a discount
        </Notification>
      )}
    </>
  );
};
